import { Button } from '@/components/shadcn/button'
import { Calendar } from '@/components/shadcn/calendar'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { IconCalendarClockOutline } from '@icons/IconCalendarClockOutline'
import { IconTimeOutline } from '@icons/IconTimeOutline'
import { format } from 'date-fns'
import { useId } from 'react'
import { getDatePickerCalendarProps } from '@/lib/datePickerCalendarProps'

interface DateTimeFilterPickerProps {
  value?: string
  onChange: (value: string | undefined) => void
  placeholder?: string
  className?: string
}

function toValidDate(value?: string) {
  if (!value) {
    return undefined
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

export function DateTimeFilterPicker({
  value,
  onChange,
  placeholder = 'Select date and time',
  className,
}: DateTimeFilterPickerProps) {
  const selectedDate = toValidDate(value)
  const id = useId()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="input"
          className={cn(
            'w-[220px] justify-start overflow-hidden text-left font-normal',
            !selectedDate && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{selectedDate ? format(selectedDate, 'PP p') : placeholder}</span>
          <IconCalendarClockOutline className="ml-auto size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" portal>
        <div className="sm:flex sm:flex-col">
          <Calendar
            mode="single"
            className="p-2"
            selected={selectedDate}
            onSelect={(date) => {
              if (!date) {
                return
              }
              const nextDate = new Date(date)
              if (selectedDate) {
                nextDate.setHours(selectedDate.getHours(), selectedDate.getMinutes(), selectedDate.getSeconds(), 0)
              }
              onChange(nextDate.toISOString())
            }}
            {...getDatePickerCalendarProps(selectedDate)}
          />
          <div className="border-t p-3">
            <div className="flex items-center gap-3">
              <Label htmlFor={id} className="text-xs">
                Time
              </Label>
              <div className="relative grow">
                <Input
                  id={id}
                  type="time"
                  step="1"
                  value={selectedDate ? format(selectedDate, 'HH:mm:ss') : ''}
                  onChange={(event) => {
                    if (!event.target.value) {
                      onChange(undefined)
                      return
                    }
                    const [hours, minutes, seconds = '0'] = event.target.value.split(':')
                    const nextDate = selectedDate ? new Date(selectedDate) : new Date()
                    nextDate.setHours(Number(hours), Number(minutes), Number(seconds), 0)
                    onChange(nextDate.toISOString())
                  }}
                  className="peer appearance-none ps-9 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
                  <IconTimeOutline size={16} aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
